<script setup lang="ts">
/* =============================================================
   ADMIN LAYOUT
   =============================================================
   Top bar + content. The main app puts navigation in a collapsible
   sidebar because it has five groups and will grow; this tool has
   a handful of screens, and a sidebar for them is mostly chrome.

   The signed-in admin's name sits in the bar on purpose. When one
   person holds both a customer account and a staff account, the
   single most useful thing the UI can tell them is which one they
   are currently acting as.
   ============================================================= */

import { RouterLink, RouterView, useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'
import Logo from '@/components/Logo.vue'
import Button from '@/components/ui/Button.vue'
import { routes } from '@/config/routes'
import { useAuth } from '@/lib/auth'

const { admin, signOut } = useAuth()
const router = useRouter()

const navItems = [
  { label: 'Dashboard', to: routes.dashboard, icon: 'dashboard' },
  { label: 'Berths', to: routes.berths, icon: 'anchor' },
  { label: 'Vessels', to: routes.vessels, icon: 'ship' },
  { label: 'Bookings', to: routes.bookings, icon: 'bookings' },
  { label: 'Companies', to: routes.companies, icon: 'building' },
] as const

async function onSignOut(): Promise<void> {
  await signOut()
  await router.push(routes.signIn)
}
</script>

<template>
  <div class="shell">
    <a class="skip-link" href="#main">Skip to content</a>

    <header class="topbar">
      <div class="bar">
        <RouterLink :to="routes.dashboard" class="brand" aria-label="River Atlas Admin">
          <Logo :size="30" />
        </RouterLink>

        <nav class="nav" aria-label="Sections">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="navLink"
            active-class="navLinkActive"
          >
            <Icon :name="item.icon" :size="17" />
            <span>{{ item.label }}</span>
          </RouterLink>
        </nav>

        <div class="account">
          <span v-if="admin" class="who">
            <span class="name">{{ admin.name }}</span>
            <span class="email">{{ admin.email }}</span>
          </span>
          <Button variant="ghost" size="sm" icon="logout" @click="onSignOut">Sign out</Button>
        </div>
      </div>
    </header>

    <main id="main" class="main">
      <div class="content">
        <RouterView />
      </div>
    </main>
  </div>
</template>

<style scoped>
.shell {
  min-height: 100dvh;
  background-color: var(--color-bg);
}

.topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-header);
  background-color: color-mix(in srgb, var(--color-surface) 88%, transparent);
  backdrop-filter: blur(var(--blur-header));
  border-bottom: var(--border-width) solid var(--color-border);
}

.bar {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  height: var(--topbar-height);
  max-width: var(--content-max);
  margin-inline: auto;
  padding-inline: var(--content-pad-x);
}

.brand {
  flex: none;
  text-decoration: none;
}

.nav {
  display: flex;
  align-items: center;
  gap: var(--space-3xs);
  min-width: 0;
  overflow-x: auto;
}

.navLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  height: var(--control-height-sm);
  padding-inline: var(--space-xs);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  white-space: nowrap;
  color: var(--color-text-muted);
  text-decoration: none;
  border-radius: var(--radius-sm);
  transition:
    color var(--dur-fast) var(--ease),
    background-color var(--dur-fast) var(--ease);
}

.navLink:hover {
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
}

.navLinkActive,
.navLinkActive:hover {
  color: var(--color-accent-hover);
  background-color: var(--color-accent-soft);
}

.account {
  display: flex;
  align-items: center;
  gap: var(--space-2xs);
  margin-inline-start: auto;
  flex: none;
}

.who {
  display: none;
  flex-direction: column;
  align-items: flex-end;
  line-height: var(--leading-tight);
}

.name {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.email {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

@media (min-width: 40rem) {
  .who {
    display: flex;
  }
}

.main {
  max-width: var(--content-max);
  margin-inline: auto;
  padding: var(--content-pad-y) var(--content-pad-x) var(--space-2xl);
}

.content {
  min-width: 0;
}

/* A phone has no room for brand, five sections and sign-out on one line:
   the nav used to squeeze to nothing. Below 40rem it drops to a second row
   of its own, full width and scrollable sideways if it ever outgrows it. */
@media (max-width: 40rem) {
  .bar {
    flex-wrap: wrap;
    height: auto;
    row-gap: var(--space-2xs);
    padding-block: var(--space-2xs);
  }

  .nav {
    order: 3;
    width: 100%;
    margin-inline: calc(var(--space-xs) * -1);
    padding-inline: var(--space-3xs);
  }
}
</style>
